import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Check, ChevronRight, X } from "lucide-react";
import { useIsMobile } from "./useIsMobile";

const INLINE_OPTION_LIMIT = 4;

type Option<T extends string> = { value: T; label: string };

export function AppSelectField<T extends string>({
  label,
  value,
  onChange,
  options,
  preferMobileControls = false,
}: {
  label: string;
  value: T;
  onChange: (value: T) => void;
  options: readonly Option<T>[];
  preferMobileControls?: boolean;
}) {
  const mobile = useIsMobile();
  const useMobileControls = mobile || preferMobileControls;
  const listId = useId();
  const selected = options.find((option) => option.value === value) ?? options[0];

  if (!useMobileControls) {
    return (
      <label className="field">
        <span>{label}</span>
        <select value={value} onChange={(event) => onChange(event.target.value as T)}>
          {options.map((option) => (
            <option key={option.value || "__empty"} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </label>
    );
  }

  if (options.length <= INLINE_OPTION_LIMIT) {
    return (
      <div className="app-select-field app-select-field--inline">
        <span className="app-select-field-label" id={listId}>
          {label}
        </span>
        <div className="app-select-options" role="listbox" aria-labelledby={listId}>
          {options.map((option) => {
            const active = option.value === value;
            return (
              <button
                className={`app-select-row ${active ? "selected" : ""}`}
                type="button"
                role="option"
                aria-selected={active}
                key={option.value || "__empty"}
                onClick={() => onChange(option.value)}
              >
                <span>{option.label}</span>
                {active && <Check size={20} aria-hidden="true" />}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <MobileSelectSheet
      label={label}
      value={value}
      selectedLabel={selected?.label ?? ""}
      options={options}
      onChange={onChange}
    />
  );
}

function MobileSelectSheet<T extends string>({
  label,
  value,
  selectedLabel,
  options,
  onChange,
}: {
  label: string;
  value: T;
  selectedLabel: string;
  options: readonly Option<T>[];
  onChange: (value: T) => void;
}) {
  const [open, setOpen] = useState(false);
  const sheetRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open]);

  const pick = (next: T) => {
    onChange(next);
    setOpen(false);
  };

  return (
    <>
      <button
        className="app-select-field app-select-field--trigger"
        type="button"
        onClick={() => setOpen(true)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`${label}: ${selectedLabel}`}
      >
        <span className="app-select-trigger-copy">
          <span className="app-select-field-label" aria-hidden="true">
            {label}
          </span>
          <span className="app-select-trigger-value-text">{selectedLabel}</span>
        </span>
        <ChevronRight className="app-select-trigger-chevron" size={18} aria-hidden="true" />
      </button>
      {open && createPortal(
        <div className="app-select-sheet-root" role="presentation" onClick={() => setOpen(false)}>
          <div
            className="app-select-sheet"
            ref={sheetRef}
            role="dialog"
            aria-modal="true"
            aria-label={label}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="app-select-sheet-header">
              <strong>{label}</strong>
              <button className="icon-button app-select-sheet-close" type="button" onClick={() => setOpen(false)} aria-label="Close">
                <X size={16} />
              </button>
            </div>
            <div className="app-select-sheet-list" role="listbox">
              {options.map((option) => {
                const active = option.value === value;
                return (
                  <button
                    className={`app-select-row ${active ? "selected" : ""}`}
                    type="button"
                    role="option"
                    aria-selected={active}
                    key={option.value || "__empty"}
                    onClick={() => pick(option.value)}
                  >
                    <span>{option.label}</span>
                    {active && <Check size={20} aria-hidden="true" />}
                  </button>
                );
              })}
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
